/**
 * E2E MEDIA pipeline for attachments (files, voice notes, photos, video).
 *
 * Reuses the philosophy of `infra/guides/FILE_TRANSFER_PROTOCOL.md` (chunking +
 * SHA-256 integrity) but adapted to Susurro's conversation channel: the
 * transport is NOT a raw DataChannel but the 1:1 double ratchet (each chunk
 * travels inside a ratcheted control message). On top of that layer we add
 * a **content key of our own** per attachment (random AES-256-GCM) so that
 * the binary is encrypted by itself (the kit's "encrypted reference" model,
 * useful for BYOS/large-media): the result on the network is `ratchet(AES-GCM(chunk))`.
 *
 * Zero-knowledge: the content key travels ENCRYPTED inside the `attach` control
 * (ratcheted); the relay only sees the opaque ciphertext of the envelope. A test
 * (`media.test.ts` + `engine-media.test.ts`) proves that neither the plaintext nor the
 * key appear in what the relay observes.
 */

import { base64ToBytes, bytesToBase64 } from "@susurro/protocol";

/** PLAINTEXT chunk size. base64+GCM+ratchet inflate ~1.8x; 48 KiB leaves the
 * envelope well below the mailbox cap (256 KiB). */
export const CHUNK_SIZE = 48 * 1024;

export type MediaKind = "file" | "voice" | "image" | "video";

/** Metadata (travels encrypted inside the `attach` control). */
export interface AttachmentMeta {
  readonly attachId: string;
  readonly name: string;
  readonly mime: string;
  readonly mediaKind: MediaKind;
  readonly size: number;
  /** SHA-256 hex of the complete PLAINTEXT (end-to-end integrity). */
  readonly sha256: string;
  /** Voice notes / video. */
  readonly durationMs?: number;
  /** Images / video. */
  readonly width?: number;
  readonly height?: number;
}

/** Encrypted attachment ready to be sent through the ratchet. */
interface EncryptedAttachment {
  /** AES-256-GCM content key (base64). Travels ENCRYPTED in the ratchet. */
  readonly keyB64: string;
  /** Encrypted chunks (base64 of `iv(12) || ciphertext`), in order. */
  readonly chunks: string[];
  readonly sha256: string;
  readonly size: number;
  readonly chunkCount: number;
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  // Copy to an own ArrayBuffer (TS 5.7: Uint8Array<ArrayBufferLike> is not a
  // guaranteed BufferSource; avoids `as`).
  const copy = new Uint8Array(bytes.length);
  copy.set(bytes);
  return copy.buffer;
}

/** SHA-256 hex of a buffer (plaintext integrity). */
export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", toArrayBuffer(bytes)));
  let hex = "";
  for (const b of digest) {
    hex += b.toString(16).padStart(2, "0");
  }
  return hex;
}

async function importKey(raw: Uint8Array, usage: KeyUsage): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", toArrayBuffer(raw), "AES-GCM", false, [usage]);
}

/**
 * Encrypts a binary with a new content key and splits it into chunks. Each chunk is
 * sealed with its own random 12-byte IV (prepended to the ciphertext).
 */
export async function encryptAttachment(plain: Uint8Array): Promise<EncryptedAttachment> {
  const rawKey = crypto.getRandomValues(new Uint8Array(32));
  const key = await importKey(rawKey, "encrypt");
  const sha256 = await sha256Hex(plain);

  const chunks: string[] = [];
  for (let offset = 0; offset < plain.length || (plain.length === 0 && chunks.length === 0);) {
    const slice = plain.subarray(offset, offset + CHUNK_SIZE);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = new Uint8Array(
      await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, toArrayBuffer(slice)),
    );
    const framed = new Uint8Array(iv.length + ct.length);
    framed.set(iv, 0);
    framed.set(ct, iv.length);
    chunks.push(bytesToBase64(framed));
    offset += CHUNK_SIZE;
    if (plain.length === 0) {
      break;
    }
  }

  return {
    keyB64: bytesToBase64(rawKey),
    chunks,
    sha256,
    size: plain.length,
    chunkCount: chunks.length,
  };
}

/**
 * Reassembles and decrypts the chunks; verifies the size and SHA-256 of the plaintext
 * (rejects a corrupted or tampered attachment).
 */
export async function decryptAttachment(input: {
  keyB64: string;
  chunks: string[];
  sha256: string;
  size: number;
}): Promise<Uint8Array> {
  const key = await importKey(base64ToBytes(input.keyB64), "decrypt");
  const parts: Uint8Array[] = [];
  let total = 0;
  for (const chunkB64 of input.chunks) {
    const framed = base64ToBytes(chunkB64);
    const iv = toArrayBuffer(framed.subarray(0, 12));
    const ct = framed.subarray(12);
    const plain = new Uint8Array(
      await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, toArrayBuffer(ct)),
    );
    parts.push(plain);
    total += plain.length;
  }
  const assembled = new Uint8Array(total);
  let pos = 0;
  for (const part of parts) {
    assembled.set(part, pos);
    pos += part.length;
  }
  if (assembled.length !== input.size) {
    throw new Error(`media: unexpected size (${assembled.length} != ${input.size})`);
  }
  const sha = await sha256Hex(assembled);
  if (sha !== input.sha256) {
    throw new Error("media: integrity check failed (SHA-256 does not match)");
  }
  return assembled;
}
